import type { Doc } from "../api/types";

/**
 * The contract between a document screen and whatever is keeping the document
 * saved. A session's lifetime is one load of one document: the screen remounts
 * it when the slug changes or a conflict forces a re-read, so nothing in here
 * has to reconcile a document that changed underneath it.
 *
 * The contract between a document screen and whatever is keeping the document
 * saved. There are two implementations: `useDocumentEditorSession`, which
 * debounces `documents.update` calls, and — when the collaboration transport
 * lands — a realtime one that persists through a websocket instead.
 *
 * The screen only ever sees this shape, so swapping implementations is a change
 * to the one line at the bottom of this file.
 */

export type SaveStatus = "idle" | "pending" | "saving" | "saved" | "conflict" | "error";

export interface DocumentSessionInput {
  documentId: string;
  /** The revision the screen loaded; the conflict token for `lastRevision`. */
  revision: number;
  initialTitle: string;
  readOnly: boolean;
  /** Reads the editor's markdown. Returns null before the editor has mounted. */
  readMarkdown: () => string | null;
  /** The document changed under us; the screen reloads and says so. */
  onConflict: (serverRevision: number | undefined) => void;
  onSaved?: (document: Doc) => void;
  onError?: (error: Error) => void;
  /** Quiet period before a save. One second (`docs/spec/revisions.md` §3). */
  debounceMs?: number;
  /** Longest a change may sit unsaved while the user keeps typing. */
  maxWaitMs?: number;
  /**
   * Whether saves carry `lastRevision`, the conflict token
   * (`docs/spec/documents.md` §3.3). True for the non-realtime editor, where a
   * stale write would clobber someone else's. False alongside a live session:
   * the collaboration server bumps `revision` on every store it makes, so a
   * title save moments later would be refused as a conflict that never was, and
   * the body — the thing a conflict check protects — is not being sent through
   * this path at all.
   */
  sendLastRevision?: boolean;
}

export interface DocumentSession {
  title: string;
  setTitle: (value: string) => void;
  /** Called by the editor on every document change. */
  noteChange: () => void;
  /** Save now and mark the edit finished (`done: true`). */
  finish: () => void;
  status: SaveStatus;
  savedAt: number | null;
  /** True when edits reach the server over a realtime transport instead. */
  realtime: boolean;
}

export type DocumentSessionHook = (input: DocumentSessionInput) => DocumentSession;

/*
 * ── The two sessions ──────────────────────────────────────────────────────
 * `useDocumentEditorSession` debounces `documents.update` and is what a
 * read-only view, a share view and any screen without a websocket gets. Beside
 * it, `useCollaboration` persists the body through the CRDT socket, and the
 * document screen then keeps this session for the title alone (the title is not
 * part of the shared document). `DocumentScreen` picks between them; both
 * implement the shape above.
 * ──────────────────────────────────────────────────────────────────────────
 */
/*
 * ── SEAM ──────────────────────────────────────────────────────────────────
 * The document screen calls `useDocumentSession` and nothing else. Pointing it
 * at the collaborative hook is the whole of the migration:
 *
 *   import { useCollaboration } from "../collaboration/useCollaboration";
 *   export const useDocumentSession: DocumentSessionHook = useCollaboration;
 *
 * A realtime session sets `realtime: true`, which is the flag the screen reads
 * to stop showing a save indicator and to stop sending `done` on unmount.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useDocumentEditorSession } from "./useDocumentEditorSession";

export { useDocumentEditorSession };

export const useDocumentSession: DocumentSessionHook = useDocumentEditorSession;
